import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHero, Section, Button } from "@/components/UI";

const TYPES = ["General Enquiry", "Practitioner Registry Application", "Healthcare Referral", "Public Complaint", "Media & Advocacy"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", inquiry_type: "General Enquiry" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Your message has been sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "", inquiry_type: "General Enquiry" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally { setSending(false); }
  };

  return (
    <>
      <PageHero overline="Contact & Complaints" testId="contact-hero"
        title="Get in touch with the Council"
        subtitle="Whether you are a member of the public, a practitioner, or a healthcare professional, our office is here to help. Public complaints regarding certified practitioners are handled confidentially." />

      <Section className="py-16 lg:py-24">
        <div className="grid lg:grid-cols-5 gap-14">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h3 className="font-serif text-3xl text-ccpm-charcoal">Council Office</h3>
              <p className="mt-3 text-ccpm-taupe font-light leading-relaxed">A national body serving practitioners and patients across all provinces and territories of Canada.</p>
            </div>
            <ul className="space-y-6">
              <li className="flex items-start gap-4"><Mail className="text-ccpm-olive mt-1" strokeWidth={1.5} size={20} /><div><p className="text-sm font-semibold text-ccpm-charcoal">Email</p><p className="text-ccpm-taupe text-sm">office@ccpm.ca</p></div></li>
              <li className="flex items-start gap-4"><Phone className="text-ccpm-olive mt-1" strokeWidth={1.5} size={20} /><div><p className="text-sm font-semibold text-ccpm-charcoal">Telephone</p><p className="text-ccpm-taupe text-sm">1-800-555-CCPM</p></div></li>
              <li className="flex items-start gap-4"><MapPin className="text-ccpm-olive mt-1" strokeWidth={1.5} size={20} /><div><p className="text-sm font-semibold text-ccpm-charcoal">Correspondence</p><p className="text-ccpm-taupe text-sm">National Secretariat, Ottawa, Ontario</p></div></li>
            </ul>
            <div className="p-7 bg-ccpm-stone border border-ccpm-border">
              <p className="overline text-ccpm-olive mb-3">Filing a complaint</p>
              <p className="text-sm text-ccpm-taupe font-light leading-relaxed">Select "Public Complaint" as your enquiry type. All complaints are reviewed by the Standards & Ethics Committee under our confidential complaints procedure.</p>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 bg-white border border-ccpm-border p-8 lg:p-10" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Full name"><input required data-testid="contact-name" value={form.name} onChange={set("name")} className="ccpm-input" /></Field>
              <Field label="Email"><input required type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className="ccpm-input" /></Field>
            </div>
            <div className="mt-6"><Field label="Enquiry type">
              <select data-testid="contact-type" value={form.inquiry_type} onChange={set("inquiry_type")} className="ccpm-input">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field></div>
            <div className="mt-6"><Field label="Subject"><input required data-testid="contact-subject" value={form.subject} onChange={set("subject")} className="ccpm-input" /></Field></div>
            <div className="mt-6"><Field label="Message"><textarea required rows={6} data-testid="contact-message" value={form.message} onChange={set("message")} className="ccpm-input resize-none" /></Field></div>
            <Button type="submit" disabled={sending} className="mt-8 w-full" data-testid="contact-submit">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send message"}
            </Button>
          </form>
        </div>
      </Section>

      <style>{`.ccpm-input{width:100%;padding:0.75rem 0.9rem;background:#FDFBF7;border:1px solid #E6DFD9;font-size:0.9rem;color:#2B2B2B;outline:none;transition:border-color .2s}.ccpm-input:focus{border-color:#6B705C}`}</style>
    </>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="overline text-ccpm-taupe block mb-2">{label}</span>{children}</label>
);
